import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import {
  AppLink,
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  LinkButton,
  ReadOnlyNote,
  ReasonField,
  SectionHeader,
  Tag,
  TextField,
  dataColumns,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
  type DataColumn,
} from "@amber/ui";
import {
  OPERATIONS_ACTIONS,
  RULE_GROUPS,
  validateRuleValue,
  type RuleRow,
  type RuleValuesResponse,
} from "../contracts/index";
import { operationsCommands, rulePreviewQuery, ruleValuesQuery } from "./queries";
import { AFTER_OPERATIONS_CHANGE, AFTER_OPERATIONS_CONFLICT } from "./keys";

const GROUP_NOTES: Record<string, string> = {
  "Source health": "precedence: Failing › Partial › Late › Unusually quiet › Healthy",
  "Cadence tiers": "set per source in Sources",
  "Status sentence": "the sentence at the top of Home",
  "Appearances as speaker": "counted on profiles",
  "Task priority": "type weight + age + impact; never edited per task",
  "Stale verification": "when identity facts need checking again",
  Attempts: "automatic attempts before an item is Failed",
};

function show(row: Pick<RuleRow, "value" | "unit">): string {
  return row.unit ? `${String(row.value)} ${row.unit}` : String(row.value);
}

function editDisabled(data: RuleValuesResponse | undefined): string | null {
  const edit = data?.actions.find((a) => a.action === OPERATIONS_ACTIONS.editRuleValue);
  return edit && !edit.enabled ? edit.reason : null;
}

const col = dataColumns<RuleRow>();

function ruleColumns(disabled: string | null, onEdit: (row: RuleRow) => void): DataColumn<RuleRow>[] {
  return [
    col.display({
      id: "rule",
      header: "Rule",
      meta: { width: 330, cellClassName: "text-fg" },
      cell: ({ row }) => row.original.label,
    }),
    col.display({
      id: "value",
      header: "Value",
      meta: { width: 210, cellClassName: "font-mono text-fg" },
      cell: ({ row }) => (
        <>
          {show(row.original)} {row.original.initial ? <Tag tone="quiet">Initial</Tag> : null}
        </>
      ),
    }),
    col.display({
      id: "used_in",
      header: "Used in",
      meta: { width: 150 },
      cell: ({ row }) => row.original.used_in,
    }),
    col.display({
      id: "last_changed",
      header: "Last changed",
      meta: { width: 170 },
      cell: ({ row: { original: row } }) =>
        row.last_changed ? `${row.last_changed.by} · ${formatDateTime(row.last_changed.at)}` : "Never changed",
    }),
    col.display({
      id: "actions",
      meta: { label: "Actions", width: 90, cellClassName: "overflow-visible text-right" },
      cell: ({ row }) => (
        <Button
          size="xs"
          disabledReason={disabled}
          aria-label={`Edit ${row.original.label}…`}
          onClick={() => {
            onEdit(row.original);
          }}
        >
          <Pencil aria-hidden="true" />
          Edit…
        </Button>
      ),
    }),
  ];
}

export function ChecksHeader() {
  const data = useQuery(ruleValuesQuery).data;
  const disabled = editDisabled(data);
  return (
    <SectionHeader
      titleId="section-title"
      title="Checks and policies"
      lead="Optional checks on top of automatic processing, the evidence-use policy that decides Eligible, and the rule values that Sources, Home, Tasks and Profiles use. Every change applies from now on and is recorded in the audit log."
      meta={
        data ? (
          <>
            {data.last_changed ? (
              <span>
                Last changed by <b>{data.last_changed.by}</b> · {formatDateTime(data.last_changed.at)}
              </span>
            ) : (
              <span>Initial values, not changed yet</span>
            )}
            <AppLink href="/settings/audit?action=operations" className="font-strong text-link">
              Audit log ({data.audit_count})
            </AppLink>
            {disabled ? <ReadOnlyNote>Read-only for your role · Admins can change this</ReadOnlyNote> : null}
          </>
        ) : null
      }
    />
  );
}

export function RuleValues() {
  const query = useQuery(ruleValuesQuery);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const data = query.data;
  const disabled = editDisabled(data);
  const columns = ruleColumns(disabled, setEditing);
  return (
    <>
      <div className="mx-4 mt-4 mb-1 flex items-baseline gap-2 max-compact:mx-2">
        <h2 className="m-0 text-lg font-semibold">Rule values</h2>
        <span className="text-md text-muted">initial values; admins can change them</span>
      </div>
      {query.isError ? (
        <Callout
          tone="attention"
          role="alert"
          className="mx-4"
          actions={
            <LinkButton
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </LinkButton>
          }
        >
          Couldn&apos;t load the rule values. {errorText(query.error)}
        </Callout>
      ) : null}
      {RULE_GROUPS.map((group) => {
        const rows = data?.rows.filter((r) => r.group === group) ?? [];
        return (
          <div key={group} className="mt-3">
            <div className="mx-4 mb-1.5 flex items-baseline gap-2 max-compact:mx-2">
              <h3 className="m-0 text-base font-semibold">{group}</h3>
              <span className="text-md text-muted">{GROUP_NOTES[group]}</span>
            </div>
            <DataTable
              label={group}
              columns={columns}
              rows={rows}
              rowId={(r) => r.rule}
              rowOptions={(r) => ({ titles: { rule: r.label } })}
              minWidth={850}
              isPending={query.isPending}
              skeletonRows={2}
              wrapClassName="flex-none rounded-b-xl border-b"
            />
          </div>
        );
      })}
      <div className="h-4 flex-none" />
      {editing ? (
        <EditRuleDialog
          row={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

function EditRuleDialog({ row, onClose }: { row: RuleRow; onClose: () => void }) {
  const toast = useToast();
  const [text, setText] = useState(String(row.value));
  const value = Number(text.trim());
  const localError = text.trim() === "" ? "Enter a number" : validateRuleValue(row, value);
  const preview = useQuery({
    ...rulePreviewQuery(row.rule, value),
    enabled: localError === null && value !== row.value,
  });
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      operationsCommands.editRule(row.rule, { base_revision: row.revision, value, reason: text }, csrf),
    invalidate: AFTER_OPERATIONS_CHANGE,
    invalidateOnConflict: AFTER_OPERATIONS_CONFLICT,
    onSuccess: () => {
      toast.show("Rule value saved");
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict" ? mutation.error : null;
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const submitted = reason.submitted;
  const valueError =
    (submitted || text !== String(row.value) ? localError : null) ??
    (submitted && value === row.value ? "Enter a different value" : null) ??
    fieldErrors["value"] ??
    null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Edit ${row.group.toLowerCase()} value`}
      onSubmit={() => {
        if (reason.submit() && localError === null && value !== row.value) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm="Save as new revision"
          pending={mutation.isPending}
          failed={mutation.isError && !conflict}
        />
      }
    >
      <DialogText>
        <b>{row.label}</b> · now {show(row)} · used in {row.used_in}
      </DialogText>
      <TextField
        label={`New value${row.unit ? ` (${row.unit})` : ""}`}
        required
        inputMode="decimal"
        value={text}
        error={valueError}
        hint={`From ${String(row.min)} to ${String(row.max)}`}
        onChange={(e) => {
          setText(e.target.value);
        }}
      />
      {preview.data ? (
        <p role="status" className="m-0 mt-2 rounded-lg bg-surface-2 px-3 py-2 text-md">
          Effect: {preview.data.effect}
        </p>
      ) : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {conflict ? (
        <Callout tone="attention" role="alert" className="mt-3" title="Someone changed this value since you opened it">
          {conflict.changedBy ? `${conflict.changedBy} saved a new revision` : "A new revision was saved"}
          {conflict.changedAt ? ` at ${formatDateTime(conflict.changedAt)}` : ""}. Close this dialog to see the current
          value; your draft is not saved.
        </Callout>
      ) : (
        <FailedCallout error={mutation.error} fieldsShown />
      )}
    </Dialog>
  );
}
